import Button from "@/components/Form/Button/Button";
import Table from "@/components/Table/table";
import { useSchemeColumnDefinition } from "./SchemeTable/SchemeColumnDefinition";
import { useGetSchemesListQuery } from "@/services/scheme/schemeReducer";
import { useParams, useNavigate } from "react-router-dom";
import { useCustomCellRenderers } from "./SchemeTable/SchemeColumnActions";
import {
  customPaginationPageSize,
  customPaginationPageSizeSelector,
  customTableLoadingData,
} from "@/components/Table/CustomPaginationConstants";
import { useMemo } from "react";
import CustomPagination from "@/components/Pagination/CustomPagination";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { havingAddPermission } from "@/utility/common";
import { CREATE_BUTTON_VARIANT } from "@/components/Images/CommonImages";
import { setComponentState1 } from "@/features/retain_state/retainStateSlice";

const Schemes = () => {
  const headerData = {
    title: "pages.configuration.schemes.tableTitle",
    description: "pages.configuration.schemes.tableDescription",
  };
  const { t } = useTranslation();
  const { caseId } = useParams();
  const navigate = useNavigate();
  const paginationPageSize: number = customPaginationPageSize;
  const paginationPageSizeSelector: number[] = customPaginationPageSizeSelector;
  let tableLoadingData = customTableLoadingData;
  const retainState = useSelector((e: any) => e.retainState.schemeListTabState);
  const dispatch = useDispatch();
  const getPaginationObj: any = useMemo(() => {
    let value: any = {
      CaseId: caseId,
      PageNumber: retainState?.PageNumber || 1,
      PageSize: retainState?.PageSize || paginationPageSize,
      SearchText: retainState?.SearchText || "",
    };
    if (retainState?.SortOrder && retainState?.SortColumn) {
      value.SortOrder = retainState?.SortOrder;
      value.SortColumn = retainState?.SortColumn;
    }
    return value;
  }, [caseId, paginationPageSize, retainState]);
  const paginationObj = getPaginationObj;
  const permissions = useSelector((state: any) => state.permissions);
  const { Scheme } = permissions;

  function handlePaginationData(data: any, columnTypesData: any) {
    dispatch(
      setComponentState1({
        entityName: "schemeListTabState",
        entityState: {
          SortOrder: data.SortOrder,
          SortColumn: data.SortColumn,
          sortedColumnTypes: columnTypesData,
        },
      })
    );
  }
  const SchemeColumnDefinition = useSchemeColumnDefinition();

  const {
    data: schemeDataByCase,
    isLoading: isSchemenDataByCaseLoading,
    isFetching,
    error,
    status
  } = useGetSchemesListQuery(paginationObj);

  const rowData = schemeDataByCase?.results ? schemeDataByCase.results : [];
  const handleEditClick = (id: string) => {
    navigate(`/case/${caseId}/scheme-manage/${id}`);
  };

  const caseRenderers = useCustomCellRenderers(handleEditClick);

  if (isSchemenDataByCaseLoading) {
    return <div>{t("global.globalNotifications.loadingMessage")}</div>;
  }

  const handleCreateNew = () => {
    navigate(`/case/${caseId}/configuration/scheme-setup`);
  };
  return (
    <div>
      <Table
        columnDefs={SchemeColumnDefinition}
        columnTypes={{ ...retainState.sortedColumnTypes }}
        handlePaginationData={handlePaginationData}
        paginationObj={paginationObj}
        cellRenderers={isFetching ? [] : rowData}
        TitleComplement={() =>
          havingAddPermission(Scheme) ? (
            <Button
              label={t("pages.configuration.schemes.createScheme")}
              variant={CREATE_BUTTON_VARIANT}
              onClickHandler={handleCreateNew}
            />
          ) : (
            <></>
          )
        }
        title={headerData.title}
        description={headerData.description}
        caseRenderers={caseRenderers}
        pagination={false}
        overlayNoRowsTemplate={
          isFetching
            ? t(tableLoadingData)
            : error
            ? t("global.globalNotifications.wentWrong")
            : t("global.globalNotifications.noRecords")
        }
      />
      <CustomPagination
       apiData={schemeDataByCase}
       apiStatus={status}
        currentpage={paginationObj.PageNumber}
        pageSize={paginationObj.PageSize}
        disableNext={!schemeDataByCase?.hasMoreRecords}
        onChangePageSize={(page: number) => {
          dispatch(
            setComponentState1({
              entityName: "schemeListTabState",
              entityState: {
                PageSize: page,
                PageNumber: 1,
              },
            })
          );
        }}
        onChangeNextPage={() => {
          dispatch(
            setComponentState1({
              entityName: "schemeListTabState",
              entityState: {
                PageNumber: paginationObj.PageNumber + 1,
              },
            })
          );
        }}
        onChangePreviousPage={() => {
          dispatch(
            setComponentState1({
              entityName: "schemeListTabState",
              entityState: {
                PageNumber: paginationObj.PageNumber - 1,
              },
            })
          );
        }}
        pageSizeSelector={paginationPageSizeSelector}
      />
    </div>
  );
};

export default Schemes;
